// Zero-import config for /private-transfer — same convention as routes.js.
// Keep claims inside the verified facts list in the private-transfer SEO plan:
// vehicles 6/14/22, preset prices, border crossings included, capture policy,
// Kenya/Tanzania coverage. Do not invent meet & greet, tolls, free waiting,
// luggage allowance, or minimum notice.

export const PRIVATE_TRANSFER_PAGE = {
  h1: 'Book a private transfer',
  faqs: [
    {
      q: 'What size vehicles do you have, and how many people fit?',
      a: 'Three sizes: 6-seater, 14-seater and 22-seater, all air-conditioned. Choose the vehicle when you request your quote; book for 1 to 22 passengers as long as the group fits.',
    },
    {
      q: 'What decides the price of my transfer?',
      // Deliberately no price list here. This answer is rendered into FAQPage
      // JSON-LD, so a figure quoted in prose is a figure Google can surface
      // long after it changed — and it sat one section below a fare card
      // saying something different. Prices live in the card, in one place.
      a: 'Your exact pickup and drop-off addresses, vehicle size, and how many border crossings the route involves. Our regular corridor routes have a set price, shown in the fare table on this page; anything else is priced from the route itself. Any supplement for a larger vehicle is already included in the figure you see.',
    },
    {
      q: 'When is my payment actually taken?',
      a: "Payment is processed when the quote is confirmed. If your route matches a known route cleanly that happens straight away. Otherwise we review first: your payment is authorised but not taken, and we take it only once we've confirmed the quote is accurate. The quote tells you which applies before you pay. You can pay by card or PayPal.",
    },
    {
      q: 'Why does my quote say a route review is needed?',
      a: "The route didn't match a standard priced route closely enough to price automatically — an unusual address, a long journey, or more than one border crossing. We check by hand so you aren't quoted the wrong price. Your payment is authorised but nothing is taken until we've confirmed it.",
    },
    {
      q: 'Can I pay in pounds instead of dollars?',
      a: 'Quotes are calculated in US dollars; switch the displayed price to GBP on the quote before booking. The pound figure uses our current rate.',
    },
    {
      q: 'Is there a luggage limit?',
      a: "No fixed bag allowance — the vehicle is yours. We ask roughly how many pieces you're carrying so we send something that fits, but the number of bags doesn't change the price.",
    },
    {
      q: 'Do you cover border crossings between Kenya and Tanzania?',
      a: 'Yes, and any crossing on your route is counted and priced into the quote. You clear exit and entry immigration in person, with your driver waiting on the other side. Routes with more crossings than standard cost more, reflected in the quote.',
    },
    {
      q: 'Can you take me into the Serengeti?',
      a: "Not through the online quote tool. Serengeti National Park — Naabi Hill Gate, Central Serengeti, Seronera, Four Seasons — needs a 4×4, so it isn't available for instant online quotes. Contact Riverside directly for a custom quote.",
    },
    {
      q: 'What areas do you cover?',
      a: 'Kenya and Tanzania. Most journeys run through Nairobi (including JKIA), Arusha and Moshi, crossing at Namanga where cross-border, but you can enter any pickup or drop-off address in either country.',
    },
    {
      q: "What's the cancellation policy?",
      a: "Cancel more than 10 days before travel for a full refund minus 6% of the fare; 3–10 days before, minus 12%; under 3 days is at Riverside Shuttle's discretion, so contact us. Refunds go back to the original card, normally within 5–10 business days.",
    },
    {
      q: 'Can I change my booking if my flight is delayed?',
      a: "Changes normally need around 24 hours' notice — contact us and we'll rearrange. If you're delayed on the day by something outside your control, such as a delayed or rescheduled flight, we'll handle it at no extra cost.",
    },
  ],
  included: [
    'The whole vehicle for your group — no other passengers, no shared timetable',
    'Door to door between the exact addresses you give us, including JKIA pickups',
    'An air-conditioned vehicle and a driver who knows the corridor',
    'One all-in price for the vehicle, not a per-person fare',
    'Any border crossing on your route, counted and priced into the quote',
    'Your own departure time, chosen by you',
  ],
  vehicles: [
    {
      id: '6-seater',
      label: '6-seater',
      capacity: 6,
      description: 'Ideal for couples, small families, and light luggage.',
      image: '/images/private-transfer-6-seater.webp',
      imageAlt: 'Toyota Alphard 6-seater private transfer vehicle',
    },
    {
      id: '14-seater',
      label: '14-seater',
      capacity: 14,
      description: 'Room for medium groups and safari bags without crowding.',
      image: '/images/private-transfer-14-seater.webp',
      imageAlt: 'Toyota HiAce 14-seater private transfer minivan',
    },
    {
      id: '22-seater',
      label: '22-seater',
      capacity: 22,
      description: 'Largest option for big groups travelling together.',
      image: '/images/private-transfer-22-seater.webp',
      imageAlt: 'Toyota Coaster 22-seater private transfer minibus',
    },
  ],
  coverage: {
    intro:
      'We cover Kenya and Tanzania. Most journeys run through the Nairobi–Arusha–Moshi corridor, crossing at Namanga when the trip is cross-border, but you can enter any pickup or drop-off address in either country.',
    hubs: [
      'Nairobi & JKIA',
      'Arusha',
      'Moshi',
      'Namanga border',
    ],
    serengetiNote:
      'Serengeti National Park destinations (Naabi Hill Gate, Central Serengeti, Seronera, Four Seasons) need a 4×4 and are not available for online quotes. Contact our offices for a custom quote.',
  },
  payment: {
    body:
      'Payment is processed when the quote is confirmed. On routes we run regularly the quote matches a known price and payment is taken immediately, so you\'re confirmed on the spot. On less common routes we review the request first — your payment is authorised but nothing is taken, and we process it only once we\'ve confirmed the quote is accurate. You\'ll see "Route review required before payment is taken" on the quote when that applies; we aim to confirm within 1 hour during office hours (longer outside).',
    conditionsLabel: 'Read the full booking conditions',
    conditionsHref: '/booking-conditions',
  },
};
